import React, { useState } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Card,
  Text,
  Button,
  Chip,
  IconButton,
  useTheme,
  Divider,
  ProgressBar,
} from 'react-native-paper';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import type { JourneyPreviewCardProps } from './JourneyPreviewTypes';
import { formatExplanationForA11y } from '@coffeeplexity/shared/utils/ai-explanations';

export function JourneyPreviewCard({
  journey,
  showExplanation = true,
  showCustomization = true,
  onCustomize,
  onAccept,
  onViewDetails,
  style,
}: JourneyPreviewCardProps) {
  const theme = useTheme();
  const [showFullExplanation, setShowFullExplanation] = useState(false);

  const { aiGenerated, generationMetadata } = journey;
  const { reasoning, confidence } = generationMetadata;

  const handleAccept = () => {
    onAccept?.();
  };

  const handleCustomize = () => {
    onCustomize?.();
  };

  const handleViewDetails = () => {
    onViewDetails?.();
  };

  const toggleExplanation = () => {
    setShowFullExplanation(!showFullExplanation);
  };

  const getSkillLevelColor = (skillLevel: string) => {
    switch (skillLevel) {
      case 'beginner':
        return theme.colors.primary;
      case 'intermediate':
        return theme.colors.secondary;
      case 'advanced':
        return theme.colors.tertiary;
      default:
        return theme.colors.outline;
    }
  };

  const formatDuration = (weeks: number) => {
    if (weeks === 1) return '1 week';
    if (weeks < 4) return `${weeks} weeks`;
    const months = Math.round(weeks / 4);
    return months === 1 ? '1 month' : `${months} months`;
  };

  return (
    <Card style={[styles.card, style]} mode='elevated'>
      <Card.Content>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.titleContainer}>
            <Text variant='headlineSmall' style={styles.title}>
              {journey.title}
            </Text>
            {aiGenerated && (
              <Chip
                icon='robot'
                mode='outlined'
                compact
                style={styles.aiChip}
                textStyle={styles.aiChipText}
              >
                AI Generated
              </Chip>
            )}
          </View>
          {showExplanation && (
            <IconButton
              icon={showFullExplanation ? 'chevron-up' : 'chevron-down'}
              size={20}
              onPress={toggleExplanation}
              accessibilityLabel={
                showFullExplanation ? 'Hide explanation' : 'Show explanation'
              }
            />
          )}
        </View>

        {/* Journey Overview */}
        <View style={styles.overview}>
          <Text variant='bodyMedium' style={styles.description}>
            {journey.description}
          </Text>

          <View style={styles.metadataRow}>
            <View style={styles.metadataItem}>
              <Text variant='labelSmall' style={styles.metadataLabel}>
                Duration
              </Text>
              <Text variant='bodyMedium' style={styles.metadataValue}>
                {formatDuration(journey.duration_weeks)}
              </Text>
            </View>

            <View style={styles.metadataItem}>
              <Text variant='labelSmall' style={styles.metadataLabel}>
                Skill Level
              </Text>
              <Chip
                mode='outlined'
                compact
                style={[
                  styles.skillChip,
                  { borderColor: getSkillLevelColor(journey.skill_level) },
                ]}
                textStyle={[
                  styles.skillChipText,
                  { color: getSkillLevelColor(journey.skill_level) },
                ]}
              >
                {journey.skill_level.charAt(0).toUpperCase() +
                  journey.skill_level.slice(1)}
              </Chip>
            </View>

            <View style={styles.metadataItem}>
              <Text variant='labelSmall' style={styles.metadataLabel}>
                Time/Week
              </Text>
              <Text variant='bodyMedium' style={styles.metadataValue}>
                {journey.time_commitment_hours_per_week}h
              </Text>
            </View>
          </View>
        </View>

        {/* Focus Areas */}
        <View style={styles.focusAreas}>
          <Text variant='labelMedium' style={styles.sectionLabel}>
            Focus Areas
          </Text>
          <View style={styles.chipContainer}>
            {journey.focus_areas.slice(0, 4).map((area, index) => (
              <Chip
                key={index}
                mode='outlined'
                compact
                style={styles.focusChip}
              >
                {area}
              </Chip>
            ))}
            {journey.focus_areas.length > 4 && (
              <Chip mode='outlined' compact style={styles.focusChip}>
                +{journey.focus_areas.length - 4} more
              </Chip>
            )}
          </View>
        </View>

        {/* AI Confidence */}
        {aiGenerated && (
          <View style={styles.confidenceSection}>
            <Text variant='labelMedium' style={styles.sectionLabel}>
              AI Confidence
            </Text>
            <ConfidenceIndicator
              confidence={confidence}
              showLabel={true}
              showDescription={false}
              size='medium'
            />
          </View>
        )}

        {/* AI Explanation */}
        {showExplanation && aiGenerated && (
          <View style={styles.explanationSection}>
            <Text variant='labelMedium' style={styles.sectionLabel}>
              Why This Journey?
            </Text>

            <Text
              variant='bodyMedium'
              style={styles.explanationText}
              accessibilityLabel={formatExplanationForA11y({
                primary: reasoning.skillLevelJustification,
                factors: reasoning.primaryFactors,
                confidence,
              })}
            >
              {reasoning.skillLevelJustification}
            </Text>

            {showFullExplanation && (
              <View style={styles.fullExplanation}>
                <Divider style={styles.divider} />

                <View style={styles.explanationItem}>
                  <Text variant='labelSmall' style={styles.explanationLabel}>
                    Duration Rationale
                  </Text>
                  <Text variant='bodySmall' style={styles.explanationDetail}>
                    {reasoning.durationRationale}
                  </Text>
                </View>

                <View style={styles.explanationItem}>
                  <Text variant='labelSmall' style={styles.explanationLabel}>
                    Content Selection
                  </Text>
                  <Text variant='bodySmall' style={styles.explanationDetail}>
                    {reasoning.contentSelectionReasoning}
                  </Text>
                </View>

                <View style={styles.explanationItem}>
                  <Text variant='labelSmall' style={styles.explanationLabel}>
                    Key Factors
                  </Text>
                  <View style={styles.factorsList}>
                    {reasoning.primaryFactors.map((factor, index) => (
                      <Text
                        key={index}
                        variant='bodySmall'
                        style={styles.factorItem}
                      >
                        • {factor}
                      </Text>
                    ))}
                  </View>
                </View>

                {reasoning.personalizedElements.length > 0 && (
                  <View style={styles.explanationItem}>
                    <Text variant='labelSmall' style={styles.explanationLabel}>
                      Personalized For You
                    </Text>
                    <View style={styles.factorsList}>
                      {reasoning.personalizedElements.map((element, index) => (
                        <Text
                          key={index}
                          variant='bodySmall'
                          style={styles.personalizedItem}
                        >
                          • {element}
                        </Text>
                      ))}
                    </View>
                  </View>
                )}
              </View>
            )}
          </View>
        )}

        {/* Journey Progress Preview */}
        <View style={styles.progressSection}>
          <Text variant='labelMedium' style={styles.sectionLabel}>
            Journey Structure
          </Text>
          <View style={styles.progressPreview}>
            <Text variant='bodySmall' style={styles.progressText}>
              {journey.weeks?.length || journey.duration_weeks} weeks planned
            </Text>
            <ProgressBar
              progress={0}
              color={theme.colors.primary}
              style={styles.progressBar}
            />
            <Text variant='bodySmall' style={styles.progressSubtext}>
              Ready to start your coffee journey
            </Text>
          </View>
        </View>
      </Card.Content>

      {/* Action Buttons */}
      <Card.Actions style={styles.actions}>
        {onViewDetails && (
          <Button
            mode='outlined'
            onPress={handleViewDetails}
            style={styles.actionButton}
          >
            View Details
          </Button>
        )}

        {showCustomization && onCustomize && (
          <Button
            mode='outlined'
            onPress={handleCustomize}
            style={styles.actionButton}
            icon='tune'
          >
            Customize
          </Button>
        )}

        {onAccept && (
          <Button
            mode='contained'
            onPress={handleAccept}
            style={styles.acceptButton}
            icon='check'
          >
            Start Journey
          </Button>
        )}
      </Card.Actions>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    margin: 16,
    elevation: 4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  titleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  title: {
    fontWeight: '600',
    flex: 1,
  },
  aiChip: {
    height: 24,
  },
  aiChipText: {
    fontSize: 10,
  },
  overview: {
    marginBottom: 16,
  },
  description: {
    marginBottom: 12,
    lineHeight: 20,
  },
  metadataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  metadataItem: {
    alignItems: 'center',
    flex: 1,
  },
  metadataLabel: {
    opacity: 0.7,
    marginBottom: 4,
  },
  metadataValue: {
    fontWeight: '500',
  },
  skillChip: {
    height: 24,
  },
  skillChipText: {
    fontSize: 12,
    fontWeight: '500',
  },
  focusAreas: {
    marginBottom: 16,
  },
  sectionLabel: {
    fontWeight: '600',
    marginBottom: 8,
    opacity: 0.8,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  focusChip: {
    height: 28,
    marginBottom: 4,
  },
  confidenceSection: {
    marginBottom: 16,
  },
  explanationSection: {
    marginBottom: 16,
  },
  explanationText: {
    lineHeight: 20,
    marginBottom: 8,
  },
  fullExplanation: {
    marginTop: 8,
  },
  divider: {
    marginVertical: 12,
  },
  explanationItem: {
    marginBottom: 12,
  },
  explanationLabel: {
    fontWeight: '600',
    marginBottom: 4,
    opacity: 0.8,
  },
  explanationDetail: {
    lineHeight: 18,
    opacity: 0.9,
  },
  factorsList: {
    marginTop: 4,
  },
  factorItem: {
    lineHeight: 16,
    marginBottom: 2,
    opacity: 0.8,
  },
  personalizedItem: {
    lineHeight: 16,
    marginBottom: 2,
    opacity: 0.9,
    fontStyle: 'italic',
  },
  progressSection: {
    marginBottom: 8,
  },
  progressPreview: {
    alignItems: 'center',
  },
  progressText: {
    marginBottom: 8,
    opacity: 0.8,
  },
  progressBar: {
    width: '100%',
    height: 6,
    borderRadius: 3,
    marginBottom: 4,
  },
  progressSubtext: {
    opacity: 0.6,
    fontStyle: 'italic',
  },
  actions: {
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 8,
  },
  actionButton: {
    minWidth: 100,
  },
  acceptButton: {
    minWidth: 120,
  },
});
